/**
 * Partners · MarketTech (operator ruling CS266, 2026-10-21) — INTERNAL.
 *
 * MarketTech shows our feed items inside its app. Every feed link goes through
 * publishing's hop, /go/mt/<article path>, which the site Worker answers with a
 * 302 to the article; `partner.marketech.clicks` counts those requests, with
 * bots, link previews, HTTP tools and non-article paths excluded. A click is a
 * request through the hop, not a reader. The hop went live 2026-10-20 05:35Z:
 * a regime start, nothing before it.
 *
 * The cross-check is `rum.referrer.pageviews` for referrer host
 * marketech.com.au. In-app clicks usually send no referrer, so expect it to be
 * far below the click count.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { getMetricSeries, type SeriesRow } from '../../lib/api/analytics.js';
import { SITE_LABEL, SITE_ORDER, addDays, toNumber } from '../../lib/analyticsShape.js';
import { VisibilityTag } from './StatTile.js';

export const PARTNER_METRIC = 'partner.marketech.clicks';
export const REFERRER_METRIC = 'rum.referrer.pageviews';
export const PARTNER_LIVE_FROM = '2026-10-20';
export const PARTNER_REFERRER_HOST = 'marketech.com.au';
const MASTHEAD_HOST: Record<string, string> = {
  bellwire: 'bellwire.news',
  orecast: 'orecast.news',
  marketfloor: 'themarketfloor.news',
};

export interface PartnerDay { day: string; bySite: Record<string, number>; total: number }
export interface PartnerArticle { site: string; path: string; clicks: number }

const sumBy = <K extends string>(rows: SeriesRow[], key: (r: SeriesRow) => K) => {
  const m = new Map<K, number>();
  for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + (toNumber(r.value) ?? 0));
  return m;
};

/** Clicks per UTC day and masthead, newest first, never before go-live. */
export function partnerDays(totals: SeriesRow[]): PartnerDay[] {
  const out = new Map<string, PartnerDay>();
  for (const r of totals) {
    if (r.metric_date < PARTNER_LIVE_FROM) continue;
    const d = out.get(r.metric_date) ?? { day: r.metric_date, bySite: {}, total: 0 };
    const v = toNumber(r.value) ?? 0;
    d.bySite[r.site] = (d.bySite[r.site] ?? 0) + v;
    d.total += v;
    out.set(r.metric_date, d);
  }
  return Array.from(out.values()).sort((a, b) => (a.day < b.day ? 1 : -1));
}

/** The most-clicked articles over [from, to], across the selected mastheads. */
export function topPartnerArticles(paths: SeriesRow[], from: string, to: string, n = 10): PartnerArticle[] {
  const inWindow = paths.filter((r) => r.metric_date >= from && r.metric_date <= to);
  const m = sumBy(inWindow, (r) => `${r.site}\u0000${r.dimension_value}`);
  return Array.from(m.entries())
    .map(([k, clicks]) => { const [site, path] = k.split('\u0000'); return { site, path, clicks }; })
    .filter((a) => a.clicks > 0)
    .sort((a, b) => b.clicks - a.clicks || a.path.localeCompare(b.path))
    .slice(0, n);
}

/** Sum of rows in [from, to], optionally only those whose dimension value matches. */
export function windowSum(rows: SeriesRow[], from: string, to: string, value?: string): number {
  return rows
    .filter((r) => r.metric_date >= from && r.metric_date <= to && (value == null || r.dimension_value === value))
    .reduce((a, r) => a + (toNumber(r.value) ?? 0), 0);
}

export function PartnerClicks({ sites, to, stale }: { sites?: string[]; to: string; stale?: boolean }) {
  const from = addDays(to, -13) < PARTNER_LIVE_FROM ? PARTNER_LIVE_FROM : addDays(to, -13);
  const weekFrom = addDays(to, -6) < PARTNER_LIVE_FROM ? PARTNER_LIVE_FROM : addDays(to, -6);
  const specs = [
    { metric: PARTNER_METRIC, dimension: 'total' },
    { metric: PARTNER_METRIC, dimension: 'path' },
    { metric: PARTNER_METRIC, dimension: 'excluded' },
    { metric: REFERRER_METRIC, dimension: 'referer_host' },
  ];
  const results = useQueries({
    queries: specs.map((s) => ({
      queryKey: ['partner-clicks', s.metric, s.dimension, from, to, sites],
      queryFn: () => getMetricSeries({ metric: s.metric, sites, dateFrom: from, dateTo: to, dimension: s.dimension }),
      placeholderData: keepPreviousData,
      staleTime: 60_000,
    })),
  });
  const [totalQ, pathQ, exclQ, refQ] = results;
  const loading = totalQ.isLoading;
  const days = useMemo(() => partnerDays(totalQ.data?.rows ?? []), [totalQ.data]);
  const top = useMemo(() => topPartnerArticles(pathQ.data?.rows ?? [], weekFrom, to), [pathQ.data, weekFrom, to]);
  const cols = SITE_ORDER.filter((s) => s in MASTHEAD_HOST && (!sites || sites.includes(s)));
  const weekBySite: Record<string, number> = {};
  for (const s of cols) weekBySite[s] = windowSum((totalQ.data?.rows ?? []).filter((r) => r.site === s), weekFrom, to);
  const weekTotal = Object.values(weekBySite).reduce((a, b) => a + b, 0);
  // A failed request is shown as unavailable, never as a measured zero.
  const excluded = exclQ.isError ? null : windowSum(exclQ.data?.rows ?? [], weekFrom, to);
  const referred = refQ.isError ? null : windowSum(refQ.data?.rows ?? [], weekFrom, to, PARTNER_REFERRER_HOST);
  const shown = (n: number | null) => (n == null ? <span className="text-warning-600" title="Request failed">unavailable</span> : n);

  return (
    <section className={`bg-surface rounded-xl border border-grey-200 p-4 flex flex-col gap-3 ${stale ? 'opacity-60' : ''} transition-opacity`} aria-label="Partners · MarketTech">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-grey-900">Partners · MarketTech</h3>
          <p className="text-xs text-grey-500 mt-0.5">
            Clicks from MarketTech&apos;s app through our feed links (/go/mt/), per UTC day. A click is a request
            through the link, not a reader; bots, link previews and HTTP tools are left out. Counting starts
            {' '}{PARTNER_LIVE_FROM} 05:35Z, when the links went live; there is nothing before it.
          </p>
        </div>
        <VisibilityTag visibility="internal" />
      </header>
      {loading ? (
        <p className="text-sm text-grey-400 py-4 text-center">Loading…</p>
      ) : totalQ.isError ? (
        <p className="text-sm text-warning-600 py-4 text-center">Clicks unavailable: the request failed.</p>
      ) : days.length === 0 ? (
        <p className="text-sm text-grey-400 py-4 text-center">No MarketTech clicks collected yet.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-grey-600">
            <span><span className="font-semibold text-grey-900 tabular-nums">{weekTotal}</span> clicks, {weekFrom} → {to}</span>
            {cols.map((s) => <span key={s}>{SITE_LABEL[s]} <span className="tabular-nums text-grey-900">{weekBySite[s] ?? 0}</span></span>)}
            <span title="Requests through the links that were not counted: bots, previews, HTTP tools, non-article paths">
              Excluded <span className="tabular-nums">{shown(excluded)}</span>
            </span>
            <span title={`Page loads whose referrer was ${PARTNER_REFERRER_HOST}. In-app clicks usually send no referrer.`}>
              Referred by {PARTNER_REFERRER_HOST} <span className="tabular-nums">{shown(referred)}</span>
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-grey-500">
                    <th className="py-1 pr-3 font-medium">Day</th>
                    <th className="py-1 pr-3 font-medium text-right">Clicks</th>
                    {cols.map((s) => <th key={s} className="py-1 pr-3 font-medium text-right">{SITE_LABEL[s]}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.day} className="border-t border-grey-100 text-grey-900">
                      <td className="py-1 pr-3 font-medium">{d.day}{d.day === PARTNER_LIVE_FROM ? <span className="text-grey-400" title="Links live from 05:35Z"> · from 05:35Z</span> : null}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{d.total}</td>
                      {cols.map((s) => <td key={s} className="py-1 pr-3 text-right tabular-nums text-grey-600">{d.bySite[s] ?? 0}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-grey-500">
                    <th className="py-1 pr-3 font-medium">Top articles, {weekFrom} → {to}</th>
                    <th className="py-1 pr-3 font-medium text-right">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {pathQ.isError ? (
                    <tr><td colSpan={2} className="py-3 text-center text-warning-600">Article breakdown unavailable: the request failed.</td></tr>
                  ) : top.length === 0 ? (
                    <tr><td colSpan={2} className="py-3 text-center text-grey-400">No article clicks in this window</td></tr>
                  ) : top.map((a) => (
                    <tr key={`${a.site}${a.path}`} className="border-t border-grey-100 text-grey-900">
                      <td className="py-1 pr-3 break-all">
                        <span className="text-grey-500">{SITE_LABEL[a.site] ?? a.site} · </span>
                        <a className="hover:underline" href={`https://${MASTHEAD_HOST[a.site] ?? ''}${a.path}`} target="_blank" rel="noreferrer">{a.path}</a>
                      </td>
                      <td className="py-1 pr-3 text-right tabular-nums">{a.clicks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <p className="text-[11px] text-grey-400">Cloudflare zone analytics · /go/mt/ 302s, request source eyeball · retained 31 days at the source, kept here for good</p>
    </section>
  );
}
